import React, { useEffect, useState } from "react";
import { Mail, Phone, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";
import { ReplyModal, LeadDetailModal } from "./Dashboard";
import { AppointmentModal } from "./Calendar";
import { isNewLead, markItemSeen } from "./leadSeen";

const STATUS_TABS = [
  { key: "new", label: "New", test: (q) => q.status === "pending" && !q.appointment_id },
  { key: "in_progress", label: "In progress", test: (q) => q.status === "in_progress" },
  { key: "done", label: "Done", test: (q) => q.status === "done" },
  { key: "cancelled", label: "Cancelled", test: (q) => q.status === "cancelled" },
  { key: "all", label: "All", test: () => true },
];

const STATUS_CHIP = {
  pending: "bg-[#1F5EFF]/15 text-[#8db2ff]",
  in_progress: "bg-amber-500/15 text-amber-300",
  done: "bg-emerald-500/15 text-emerald-300",
  cancelled: "bg-red-500/15 text-red-300",
};

export default function Leads() {
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("new");
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState(null);
  const [leadDetail, setLeadDetail] = useState(null);
  const [bookPrefill, setBookPrefill] = useState(null);

  const load = () => {
    setLoading(true);
    CRM_API.get("/quotes")
      .then(({ data }) => setQuotes((Array.isArray(data) ? data : [])
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setLeadStatus = async (q, status) => {
    try {
      await CRM_API.patch(`/quotes/${q.id}`, { status });
      setQuotes((prev) => prev.map((x) => (x.id === q.id ? { ...x, status } : x)));
      setLeadDetail((prev) => (prev && prev.id === q.id ? { ...prev, status } : prev));
      if (status === "done") setLeadDetail(null);
      toast.success("Status updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteLead = async (q) => {
    try {
      await CRM_API.delete(`/quotes/${q.id}`);
      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
      setLeadDetail((prev) => (prev && prev.id === q.id ? null : prev));
      toast.success("Request deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openBookingFromQuote = async (q) => {
    let customer_id = q.customer_id || "";
    if (!customer_id && q.phone) {
      try {
        const digits = (s) => (s || "").replace(/\D/g, "");
        const { data } = await CRM_API.get(`/customers?q=${encodeURIComponent(q.phone.trim())}&limit=10`);
        const m = (Array.isArray(data) ? data : []).find((c) => digits(c.phone) && digits(c.phone) === digits(q.phone));
        if (m) customer_id = m.id;
      } catch { /* staff creates the customer inline */ }
    }
    const snap = q.pricing_snapshot || {};
    const serviceNames = (snap.services || []).map((s) => s.name).filter(Boolean);
    if (!serviceNames.length && q.service) serviceNames.push(...q.service.split("+").map((s) => s.trim()).filter(Boolean));
    setBookPrefill({
      quote: q,
      prefill: {
        customer_id,
        scheduled_at: q.preferred_date ? `${q.preferred_date}T${q.preferred_time || "10:00"}:00` : undefined,
        estimated_price: q.estimated_price || snap.total || 0,
        notes: `Website request ${q.id}${q.service ? ` — ${q.service}` : ""}`,
        service_names: serviceNames,
      },
    });
  };

  const active = STATUS_TABS.find((t) => t.key === tab) || STATUS_TABS[0];
  const rows = quotes.filter(active.test);
  const newCount = quotes.filter(STATUS_TABS[0].test).length;

  return (
    <div data-testid="crm-leads">
      <PageHeader eyebrow="From the website" title="Leads"
        subtitle="Every quote request submitted on wtddetailing.ca — reply, book, or close them here." />

      <div className="px-6 md:px-10 pb-16">
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {STATUS_TABS.map((t) => {
            const n = quotes.filter(t.test).length;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`leads-tab-${t.key}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  tab === t.key ? "bg-[#1F5EFF] text-white" : "border border-white/15 text-white/60 hover:text-white hover:border-white/40"}`}>
                {t.label} ({n})
              </button>
            );
          })}
          <button onClick={load} data-testid="leads-refresh"
            className="ml-auto p-2 rounded-lg border border-white/10 text-white/50 hover:text-white hover:bg-white/5">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {loading && quotes.length === 0 ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 rounded-xl border border-dashed border-white/12 text-center text-white/40 text-sm" data-testid="leads-empty">
            {tab === "new" ? "No new requests — you're all caught up." : "Nothing here."}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((q) => (
              <button key={q.id} onClick={() => { setLeadDetail(q); markItemSeen(q.id); }}
                data-testid={`lead-row-${q.id}`}
                className={`w-full text-left p-4 rounded-xl border transition-colors flex items-center gap-4 ${
                  isNewLead(q) ? "bg-[#1F5EFF]/8 border-[#1F5EFF]/30 hover:border-[#1F5EFF]/60" : "bg-white/[0.03] border-white/8 hover:border-white/20"}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm font-medium truncate">{q.name}</span>
                    {isNewLead(q) && <span className="w-1.5 h-1.5 rounded-full bg-[#1F5EFF] animate-pulse shrink-0" />}
                  </div>
                  <div className="text-white/50 text-xs truncate mt-0.5">{q.vehicle} · {q.service}</div>
                  <div className="text-white/30 text-[10px] mt-0.5 font-mono-alt">
                    {(q.created_at || "").slice(0, 16).replace("T", " ")}
                    {q.preferred_date ? ` · wants ${q.preferred_date}${q.preferred_time ? " " + q.preferred_time : ""}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {q.estimated_price ? <span className="text-white/70 text-sm tabular-nums">${Number(q.estimated_price).toFixed(0)}</span> : null}
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_CHIP[q.status] || "bg-white/10 text-white/50"}`}>
                    {q.status}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {replyTo && <ReplyModal quote={replyTo} onClose={() => setReplyTo(null)} />}
      {leadDetail && (
        <LeadDetailModal
          quote={leadDetail}
          onClose={() => setLeadDetail(null)}
          onReply={(q) => { setLeadDetail(null); setReplyTo(q); }}
          onSetStatus={(status) => setLeadStatus(leadDetail, status)}
          onBook={openBookingFromQuote}
          onDelete={deleteLead}
        />
      )}
      {bookPrefill && (
        <AppointmentModal
          prefill={bookPrefill.prefill}
          onClose={() => setBookPrefill(null)}
          onSaved={async (apptId) => {
            const q = bookPrefill.quote;
            setBookPrefill(null);
            if (apptId) {
              try {
                await CRM_API.patch(`/quotes/${q.id}`, { status: "in_progress", appointment_id: apptId });
                setQuotes((prev) => prev.map((x) => (x.id === q.id ? { ...x, status: "in_progress", appointment_id: apptId } : x)));
                toast.success("Quote linked to the new appointment");
              } catch { /* appointment saved; link failed silently */ }
            }
          }}
        />
      )}
    </div>
  );
}
